import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { formatISO, getYear, isValid, set, startOfToday, sub, subYears } from 'date-fns';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import DocumentsForm from '../components/DocumentsForm';

const Documents = ({ onClose, next, prev, trailerState, setTrailerState }) => {
  const { documents, effectiveDate } = trailerState;
  const [validationText, setValidationText] = useState('');

  const evaluateDate = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const givenDate = new Date(date);
    const currentTime = new Date();
    const currentDate = startOfToday();
    const currentDateTime = set(currentDate, {
      hours: currentTime.getHours(),
      minutes: currentTime.getMinutes(),
      seconds: currentTime.getSeconds(),
    });
    const oneYearAgo = sub(currentDateTime, { years: 1 });
    const dateEval = givenDate.getTime() >= oneYearAgo.getTime() && getYear(date) <= 2099;
    return dateEval;
  };

  const todayOrGreater = date => {
    if (R.isNil(date)) return false;

    const dateCheckPass = evaluateDate(date);
    return dateCheckPass;
  };

  const isInvalid = R.anyPass([
    R.o(todayOrGreater, R.prop('effectiveDate')),
    R.o(R.isNil, R.prop('effectiveDate')),
    R.o(R.is(Object), R.prop('effectiveDate')),
    R.o(R.isNil, R.path(['documents', 'method27', 'documentId'])),
    R.o(R.isNil, R.path(['documents', 'wetTest', 'documentId'])),
  ])(trailerState);

  // prettier-ignore
  const handleMethod27Upload = R.compose(
    setTrailerState,
    R.assocPath(['documents', 'method27']),
  );

  // prettier-ignore
  const handleWetTestUpload = R.compose(
    setTrailerState,
    R.assocPath(['documents', 'wetTest']),
  );

  const validateEffectiveDate = date => {
    if (!date) setValidationText('A valid date is Required');
    else if (date < subYears(startOfToday(), 1))
      setValidationText('Must be within 1 year of current date');
    else setValidationText('');
  };

  const nullifyEffectiveDate = () => setTrailerState(R.assoc('effectiveDate')(null));

  // eslint-disable-next-line consistent-return
  const handleEffectiveDateChange = dateInput => {
    validateEffectiveDate(dateInput);
    if (R.isNil(dateInput)) nullifyEffectiveDate();

    if (isValid(dateInput)) {
      const newEffectiveDate = evaluateDate(dateInput) ? formatISO(dateInput) : dateInput;
      setTrailerState(R.assoc('effectiveDate')(newEffectiveDate));
    } else {
      nullifyEffectiveDate();
    }
  };

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={onClose}
      title="New Trailer"
    >
      <ProgressBar
        label="Effective Date and Documents (2 of 5)"
        sx={{
          '&.MuiLinearProgress-colorPrimary > .MuiLinearProgress-bar': {
            borderRadius: '10px',
          },
        }}
        value={40}
      />
      <Stack sx={{ mt: 3 }}>
        <DocumentsForm
          documents={documents}
          effectiveDate={effectiveDate}
          onEffectiveDateChange={handleEffectiveDateChange}
          onMethod27Upload={handleMethod27Upload}
          onWetTestUpload={handleWetTestUpload}
          title="Upload Required Documents"
          validationText={validationText}
        />
      </Stack>
    </DrawerLayoutWrapper>
  );
};

Documents.propTypes = {
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  setTrailerState: PropTypes.func.isRequired,
  trailerState: PropTypes.shape({
    documents: PropTypes.shape({}),
    effectiveDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  }).isRequired,
};

export default Documents;
